import React, { useContext, useState } from 'react';
import { MainContext } from '../context/MainContext';
import { useSelector } from 'react-redux';
import { placeorder } from '../apis';
import { Link } from 'react-router-dom';
import PlaceOrderForm from '../components/PlaceOrderForm';
import CartTotal from '../components/CartTotal';
import OrderReview from '../components/OrderReview';
import { calculateCartTotal } from '../utils/CartTotalCalc';

const PlaceOrder = () => {
  const { products, token, setOrderPlaced } = useContext(MainContext);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const [loading, setLoading] = useState(false);
  // Set once the backend confirms the order request was saved — holds the
  // reference number shown on the confirmation screen. null means no
  // successful submission yet (still filling the form, or it failed).
  const [orderConfirmation, setOrderConfirmation] = useState(null);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
    phone: ""
  });

  const cartItemsArray = cartItems
    ? Object.keys(cartItems)
        .map(id => {
          const product = products.find(product => product._id === id);
          return product ? { ...product, qty: cartItems[id] } : null;
        })
        .filter(item => item !== null)
    : [];

  const placeOrder = async () => {
    const {
      firstName, lastName, email, street,
      city, state, pincode, phone
    } = data;

    if (
      firstName && lastName && email && street &&
      city && state && pincode && phone
    ) {
      if (Object.keys(cartItems).length > 0) {
        const { finalAmount } = calculateCartTotal(cartItemsArray);
        const orderData = {
          address: data,
          items: cartItemsArray,
          amount: finalAmount
        };

        try {
          setLoading(true); // Show loader
          const response = await placeorder(orderData, {
            headers: { token }
          });
          setLoading(false); // Hide loader
          if (response.data.success) {
            setOrderPlaced(true);
            setOrderConfirmation({ orderId: response.data.orderId });
          } else {
            // Surface the backend's actual reason (e.g. an invalid item/
            // address it caught) instead of a generic message when it sends one.
            alert(response.data.message || "Failed to place order. Please try again.");
          }
        } catch (error) {
          setLoading(false);
          // A non-2xx response (e.g. a validation error) still carries a
          // message on error.response.data — a true network failure won't.
          // Either way, this branch never marks the order as placed.
          alert(error.response?.data?.message || "Failed to place order. Please try again.");
          // Log only the message — the full axios error object includes the
          // request config, which carries both the JWT (sent as the "token"
          // header) and the order/address details.
          console.error("Error placing order:", error.message);
        }
      } else {
        alert("Cart is empty");
      }
    } else {
      alert("Please fill all the fields");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center z-50">
        <div className="loader mb-4 border-4 border-gray-300 border-t-blue-500 rounded-full w-12 h-12 animate-spin"></div>
        <p className="text-lg font-semibold text-gray-800">Placing order, please wait...</p>
      </div>
    );
  }

  if (orderConfirmation) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow space-y-4">
          <div className="text-5xl">✅</div>
          <h2 className="text-2xl font-bold text-gray-800">Order Request Received</h2>
          {orderConfirmation.orderId && (
            <p className="text-gray-600">
              Reference #: <span className="font-mono font-semibold text-gray-800">{orderConfirmation.orderId}</span>
            </p>
          )}
          <p className="text-gray-600">
            This is a confirmation that we've received your order <strong>request</strong> — no online payment
            has been taken. Our team will contact you shortly to confirm the details.
          </p>
          <p className="text-gray-600">A copy has also been sent to your registered email.</p>
          <Link to="/">
            <button className="mt-2 bg-[#2D3092] text-white font-semibold py-2 px-6 rounded-lg hover:bg-opacity-90 transition">
              Continue Shopping
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 px-4 sm:px-6 md:px-10 py-6 max-w-7xl mx-auto">
      {/* Left: Form */}
      <div className="w-full lg:w-2/3 bg-white p-4 sm:p-6 rounded-lg shadow">
        <PlaceOrderForm data={data} setData={setData} />
      </div>

      {/* Right: Review + Cart Summary */}
      <div className="w-full lg:w-1/3 flex flex-col gap-6">
        <OrderReview cartItemsArray={cartItemsArray} />
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow h-fit">
          <CartTotal cartItemsArray={cartItemsArray} placeOrder={placeOrder} />
          <p className="text-xs text-gray-500 text-center mt-3">
            This submits an order <strong>request</strong> — no online payment is collected here.
            Our team will contact you to confirm details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlaceOrder;
